import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import { getLeavePayoutReport, exportLeavePayoutReport } from '../../services/leaveBalanceReportService';
import { getDepartments } from '../../services/departmentService';
import { getLocations } from '../../services/locationService';
import { formatDate, parseTableParams } from '../../utils/Utils';
import PersonCell from '../../components/ui/PersonCell';
import { FilterCard, FilterField, SearchField, ClearFilters } from './ReportFilterBar';

// Nakde Çevrilecek İzinler — devir tavanlı türde yıl dönümünde tavanı aşan günler ve
// süresi dolan devir (bkz. backend Common/LeaveAccrualEngine.cs). Kapsam İzin Bakiye
// Raporu ile AYNI (LeaveScopeResolver: LM.Write → tüm kiracı, amir → alt ağaç); sayfa
// o raporun araç çubuğundan açılır. Yapı LeaveBalanceReportPage'in aynısı: süzgeç şeridi
// tablo ÜSTÜNDE, sayfalama/sıralama sunucuda.

const ScopeHint = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
`;

const Days = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// DataTable'ın sayfa seçenekleri 10/20/50 — varsayılan onlardan biri olmalı.
const SAYFA_BOYU = 20;

const REASON_COLORS = { cap: '#6a1b9a', expiry: '#f57c00' };
const PROJECTED_COLOR = '#0291ef';
const REALIZED_COLOR = '#2e7d32';

export default function LeavePayoutReportPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const currentYear = new Date().getFullYear();

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scopeError, setScopeError] = useState(null);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: SAYFA_BOYU });
  const [page, setPage] = useState({ pageNumber: 1, pageSize: SAYFA_BOYU });
  const [sort, setSort] = useState({ sortField: 'date', sortDirection: 'asc' });
  const [year, setYear] = useState(currentYear);
  const [departmentId, setDepartmentId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [name, setName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');

  // Süzgeç ya da sıralama değişince 1. sayfaya dönülür — gerekçe LeaveBalanceReportPage'de.
  const ilkSayfa = useCallback(() => setPage((p) => (p.pageNumber === 1 ? p : { ...p, pageNumber: 1 })), []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(name.trim());
      ilkSayfa();
    }, 400);
    return () => clearTimeout(timer);
  }, [name, ilkSayfa]);

  // Gelecek yıl da seçilebilir: bir sonraki yıl dönümü yıl başını aşıyorsa tahmini satır orada.
  // Şeritteki seçiciler etiketsiz (kompakt) olduğundan seçenek adı "Yıl 2026" — değer tek başına sayı kalmaz.
  const yearOptions = useMemo(
    () => Array.from({ length: 7 }, (_, i) => currentYear + 1 - i)
      .map((y) => ({ id: y, name: t('leavePayoutReport.yearValue', { year: y }) })),
    [currentYear, t],
  );
  const searchYears = useCallback(async (query) => {
    const q = (query || '').trim();
    return q ? yearOptions.filter((o) => o.name.includes(q)) : yearOptions;
  }, [yearOptions]);
  const yearById = useCallback(
    async (id) => yearOptions.find((o) => o.id === Number(id)) || { id, name: t('leavePayoutReport.yearValue', { year: id }) },
    [yearOptions, t],
  );

  const filterParams = useMemo(
    () => ({ year, departmentId, locationId, name: debouncedName, ...sort }),
    [year, departmentId, locationId, debouncedName, sort],
  );

  const queryParams = useMemo(() => ({ ...filterParams, ...page }), [filterParams, page]);

  // Yalnız EN SON isteğin yanıtı tabloya yazılır (sırasız dönen yanıtlar eski süzgecin sonucunu göstermesin).
  const sonIstek = useRef(0);

  const fetchData = useCallback((params) => {
    const istek = sonIstek.current + 1;
    sonIstek.current = istek;
    setFetching(true);
    getLeavePayoutReport(params)
      .then((res) => {
        if (istek !== sonIstek.current) return;
        // api.js GET'leri reject etmez — sinyal res.data.status.
        if (!res.data?.status) {
          setScopeError(res.data?.message || 'leaveBalanceReport.noScope');
          setData([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: params.pageSize || SAYFA_BOYU });
          return;
        }
        setScopeError(null);
        const result = res.data?.data;
        const items = result?.data || [];
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || SAYFA_BOYU,
        });
      })
      .finally(() => { if (istek === sonIstek.current) setFetching(false); });
  }, []);

  useEffect(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  const handleTableChange = useCallback((action, tableState) => {
    if (action === 'sort') {
      const { name: colName, direction } = tableState.sortOrder || {};
      if (colName) {
        setSort({ sortField: colName, sortDirection: direction || 'asc' });
        ilkSayfa();
      }
      return;
    }
    const parsed = parseTableParams(tableState, []);
    setPage({ pageNumber: parsed.pageNumber || 1, pageSize: parsed.pageSize || SAYFA_BOYU });
  }, [ilkSayfa]);

  const handleYear = useCallback((v) => { setYear(v ? Number(v) : currentYear); ilkSayfa(); }, [currentYear, ilkSayfa]);
  const handleDepartment = useCallback((v) => { setDepartmentId(v); ilkSayfa(); }, [ilkSayfa]);
  const handleLocation = useCallback((v) => { setLocationId(v); ilkSayfa(); }, [ilkSayfa]);
  const clearFilters = useCallback(() => {
    setYear(currentYear);
    setDepartmentId(null);
    setLocationId(null);
    setName('');
    setDebouncedName('');
    ilkSayfa();
  }, [currentYear, ilkSayfa]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await exportLeavePayoutReport(filterParams);
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nakde-cevrilecek-izinler.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [filterParams]);

  const searchDepartments = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await getDepartments(params);
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchLocations = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await getLocations(params);
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const columns = useMemo(() => {
    const unitDay = t('leaveBalanceReport.unitDay').toLowerCase();
    const dateCell = (v) => (v ? formatDate(v, lang) : '-');
    return [
    { name: 'fullName', label: t('leaveBalanceReport.employee'), options: {
      filter: false, sort: true,
      customBodyRender: (v, tableMeta) => {
        const row = data[tableMeta.rowIndex];
        return (
          <PersonCell
            name={row && !row.isActive ? `${v} (${t('leavePayoutReport.leftCompany')})` : v}
            profilePicture={row?.profilePicture}
          />
        );
      },
    } },
    { name: 'departmentName', label: t('leaveBalanceReport.department'), options: { filter: false, sort: true, customBodyRender: (v) => v || '-' } },
    { name: 'leaveVariationName', label: t('leaveBalanceReport.leaveType'), options: { filter: false, sort: true } },
    {
      name: 'serviceYearStart', label: t('leavePayoutReport.serviceYear'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row ? `${dateCell(row.serviceYearStart)} – ${dateCell(row.serviceYearEnd)}` : '-';
        },
      },
    },
    { name: 'date', label: t('leavePayoutReport.date'), options: { filter: false, sort: true, customBodyRender: dateCell } },
    {
      name: 'reason', label: t('leavePayoutReport.reason'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (
          <Badge $color={REASON_COLORS[v] || REASON_COLORS.cap}>
            {v === 'expiry' ? t('leavePayoutReport.reasonExpiry') : t('leavePayoutReport.reasonCap')}
          </Badge>
        ),
      },
    },
    {
      name: 'isProjected', label: t('leavePayoutReport.status'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (
          <Badge $color={v ? PROJECTED_COLOR : REALIZED_COLOR}>
            {v ? t('leavePayoutReport.projected') : t('leavePayoutReport.realized')}
          </Badge>
        ),
      },
    },
    { name: 'cap', label: t('leavePayoutReport.cap'), options: { filter: false, sort: false, customBodyRender: (v) => (v === null || v === undefined ? '-' : `${Number(v)} ${unitDay}`) } },
    { name: 'balanceBefore', label: t('leavePayoutReport.balanceBefore'), options: { filter: false, sort: false, customBodyRender: (v) => `${Number(v ?? 0)} ${unitDay}` } },
    { name: 'days', label: t('leavePayoutReport.days'), options: { filter: false, sort: true, customBodyRender: (v) => <Days>{`${Number(v ?? 0)} ${unitDay}`}</Days> } },
    ];
  }, [t, lang, data]);

  return (
    <>
      <FilterCard>
        <FilterField $w="150px">
          <AsyncGenericSelect
            compact
            label={t('leavePayoutReport.year')}
            value={year}
            onChange={handleYear}
            onSearch={searchYears}
            onGetById={yearById}
          />
        </FilterField>
        <FilterField>
          <AsyncGenericSelect
            compact
            label={t('leaveBalanceReport.department')}
            value={departmentId}
            onChange={handleDepartment}
            onSearch={searchDepartments}
          />
        </FilterField>
        <FilterField>
          <AsyncGenericSelect
            compact
            label={t('leaveBalanceReport.location')}
            value={locationId}
            onChange={handleLocation}
            onSearch={searchLocations}
          />
        </FilterField>
        <FilterField $w="260px">
          <SearchField value={name} onChange={setName} placeholder={t('leaveBalanceReport.searchName')} />
        </FilterField>
        <ClearFilters visible={!!(year !== currentYear || departmentId || locationId || name)} onClick={clearFilters} />
      </FilterCard>

      {scopeError ? (
        <ScopeHint>{t(scopeError)}</ScopeHint>
      ) : (
        <DataTable
          title={t('leavePayoutReport.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          onTableChanged={handleTableChange}
          disableFilters
          toolbarActions={(
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              <Download size={16} /> {t('leaveBalanceReport.exportExcel')}
            </Button>
          )}
        />
      )}
    </>
  );
}
